import type { GameplayOutcome, GameplayOutcomeType, ReferenceData } from '../../shared/types';

const labels: Record<GameplayOutcomeType, string> = {
  damage: 'Schaden',
  damage_over_time: 'Schaden über Zeit',
  healing: 'Heilung',
  healing_over_time: 'Heilung über Zeit',
  buff: 'Buff',
  debuff: 'Debuff',
  world_interaction: 'Weltinteraktion',
};

const descriptions: Record<GameplayOutcomeType, string> = {
  damage: 'Zieht beim Treffer sofort Lebenspunkte ab.',
  damage_over_time: 'Verteilt Schaden in standardisierten Sekunden-Ticks.',
  healing: 'Stellt beim Treffer sofort Lebenspunkte wieder her.',
  healing_over_time: 'Stellt über mehrere Sekunden Lebenspunkte wieder her.',
  buff: 'Erhöht vorübergehend oder passiv einen Charakterwert.',
  debuff: 'Senkt vorübergehend einen Charakterwert.',
  world_interaction: 'Löst eine standardisierte Reaktion in der Spielwelt aus.',
};

const healthOutcomeTypes: GameplayOutcomeType[] = ['damage', 'damage_over_time', 'healing', 'healing_over_time'];
const timedHealthOutcomeTypes: GameplayOutcomeType[] = ['damage_over_time', 'healing_over_time'];

export function blankOutcome(type: GameplayOutcomeType, passive = false): GameplayOutcome {
  return {
    type,
    baseValue: healthOutcomeTypes.includes(type) ? 1 : null,
    valueCalculation: 'flat',
    durationSeconds: timedHealthOutcomeTypes.includes(type) || (!passive && ['buff', 'debuff'].includes(type)) ? 15 : null,
    attributeKey: ['buff', 'debuff'].includes(type) ? 'attribute.outgoing_damage' : null,
    modifierPercent: ['buff', 'debuff'].includes(type) ? 10 : null,
    interactionKey: type === 'world_interaction' ? 'interaction.extinguish' : null,
    strengthMode: 'primary',
    fixedStrength: null,
  };
}

export function GameplayOutcomeEditor({
  reference,
  outcomes,
  onChange,
  passive = false,
  periodicDisabled = false,
  fieldErrors = {},
}: {
  reference: ReferenceData;
  outcomes: GameplayOutcome[];
  onChange: (next: GameplayOutcome[]) => void;
  passive?: boolean;
  periodicDisabled?: boolean;
  fieldErrors?: Record<string,string>;
}) {
  const available: GameplayOutcomeType[] = passive
    ? ['buff']
    : periodicDisabled
      ? ['damage', 'healing', 'world_interaction']
      : ['damage', 'damage_over_time', 'healing', 'healing_over_time', 'buff', 'debuff', 'world_interaction'];
  const hasDisallowedTickOutcome = periodicDisabled && outcomes.some((outcome) =>
    timedHealthOutcomeTypes.includes(outcome.type) || outcome.type === 'buff' || outcome.type === 'debuff');

  const errorFor = (index:number, field:string) => fieldErrors[`outcomes.${index}.${field}`] ?? null;

  function patch(index: number, change: Partial<GameplayOutcome>) {
    onChange(outcomes.map((outcome, row) => row === index ? { ...outcome, ...change } : outcome));
  }

  return (
    <div className="outcome-editor">
      <div className="outcome-add-panel">
        <strong>Wirkung hinzufügen</strong>
        <span>{periodicDisabled ? 'Diese Ausführung tickt bereits selbst. Nutze normalen Schaden oder normale Heilung pro Tick.' : 'Eine Fähigkeit darf mehrere Wirkungen kombinieren, zum Beispiel Schaden und „Flamme löschen“.'}</span>
        {hasDisallowedTickOutcome && <div className="field-callout warning">Entferne bestehende DoT-/HoT-, Buff- oder Debuff-Wirkungen. Tickende Ausführungen verwenden hier direkten Schaden, direkte Heilung oder eine Weltinteraktion.</div>}
        <div className="outcome-add-grid">
          {available.map((type) => <button type="button" className="outcome-add-button" key={type} onClick={() => onChange([...outcomes, blankOutcome(type, passive)])}><strong>+ {labels[type]}</strong><small>{descriptions[type]}</small></button>)}
        </div>
      </div>

      <div className="outcome-list">
        {outcomes.map((outcome, index) => {
          const isHealing = outcome.type.startsWith('healing');
          const isHealthOutcome = healthOutcomeTypes.includes(outcome.type);
          const isTimedHealthOutcome = timedHealthOutcomeTypes.includes(outcome.type);
          return (
            <div className="outcome-card" key={outcome.id ?? `${outcome.type}-${index}`}>
              <div className="outcome-card-heading">
                <div><span className="outcome-index">{index + 1}</span><div><span className="eyebrow">Wirkung {index + 1}</span><h3>{labels[outcome.type]}</h3><p>{descriptions[outcome.type]}</p></div></div>
                <button type="button" className="button danger-ghost small" onClick={() => onChange(outcomes.filter((_, row) => row !== index))}>Entfernen</button>
              </div>
              {Object.entries(fieldErrors).filter(([field])=>field.startsWith(`outcomes.${index}.`)).map(([field,message])=><small className="field-error outcome-field-error" key={field}>{message}</small>)}

              {isHealthOutcome && (
                <div className="form-grid three outcome-fields">
                  <label>{isHealing ? 'Basisheilung' : 'Basisschaden'}
                    <input aria-invalid={Boolean(errorFor(index,'baseValue'))} type="number" min="0.01" step="0.01" value={outcome.baseValue ?? ''} onChange={(event) => patch(index, { baseValue: event.target.value === '' ? null : Number(event.target.value) })}/>
                    <small className="field-help">Bei Prozentberechnung entspricht dieser Wert dem Prozentsatz.</small>
                  </label>
                  <label>Berechnung
                    <select aria-invalid={Boolean(errorFor(index,'valueCalculation'))} value={outcome.valueCalculation} onChange={(event) => patch(index, { valueCalculation: event.target.value as GameplayOutcome['valueCalculation'] })}>
                      <option value="flat">Fester HP-Wert</option>
                      <option value="target_max_hp_percent">% der maximalen Ziel-HP</option>
                      <option value="instigator_max_hp_percent">% der maximalen Anwender-HP</option>
                    </select>
                  </label>
                  {isTimedHealthOutcome && <label>Dauer (Sekunden)<input aria-invalid={Boolean(errorFor(index,'durationSeconds'))} type="number" min="0.1" step="0.1" value={outcome.durationSeconds ?? ''} onChange={(event) => patch(index, { durationSeconds: event.target.value === '' ? null : Number(event.target.value) })}/><small className="field-help">Ausführung standardisiert einmal pro Sekunde.</small></label>}
                </div>
              )}

              {(outcome.type === 'buff' || outcome.type === 'debuff') && (
                <div className="form-grid three outcome-fields">
                  <label>Charakterwert<select aria-invalid={Boolean(errorFor(index,'attributeKey'))} value={outcome.attributeKey ?? ''} onChange={(event) => patch(index, { attributeKey: event.target.value || null })}><option value="">Auswählen …</option>{reference.attributes.map((entry) => <option key={entry.key} value={entry.key}>{entry.label}</option>)}</select></label>
                  <label>{outcome.type === 'buff' ? 'Bonus' : 'Malus'} in %<input aria-invalid={Boolean(errorFor(index,'modifierPercent'))} type="number" min="0.01" step="0.1" value={outcome.modifierPercent ?? ''} onChange={(event) => patch(index, { modifierPercent: event.target.value === '' ? null : Number(event.target.value) })}/></label>
                  {!passive && <label>Dauer (Sekunden)<input aria-invalid={Boolean(errorFor(index,'durationSeconds'))} type="number" min="0.1" step="0.1" value={outcome.durationSeconds ?? ''} onChange={(event) => patch(index, { durationSeconds: event.target.value === '' ? null : Number(event.target.value) })}/></label>}
                  {passive && <div className="field-callout success">Wirkt dauerhaft, solange das Combat Kit aktiv ist.</div>}
                </div>
              )}

              {outcome.type === 'world_interaction' && (
                <div className="form-grid three outcome-fields">
                  <label>Interaktion<select aria-invalid={Boolean(errorFor(index,'interactionKey'))} value={outcome.interactionKey ?? ''} onChange={(event) => patch(index, { interactionKey: event.target.value || null })}><option value="">Auswählen …</option>{reference.worldInteractions.map((entry) => <option key={entry.key} value={entry.key}>{entry.label}</option>)}</select></label>
                  <label>Stärke<select aria-invalid={Boolean(errorFor(index,'strengthMode'))} value={outcome.strengthMode} onChange={(event) => patch(index, { strengthMode: event.target.value as GameplayOutcome['strengthMode'] })}><option value="primary">Aus Schaden / Heilung ableiten</option><option value="fixed">Feste Interaktionsstärke</option></select></label>
                  {outcome.strengthMode === 'fixed' && <label>Feste Stärke<input aria-invalid={Boolean(errorFor(index,'fixedStrength'))} type="number" min="0.01" step="0.1" value={outcome.fixedStrength ?? ''} onChange={(event) => patch(index, { fixedStrength: event.target.value === '' ? null : Number(event.target.value) })}/></label>}
                  {outcome.strengthMode === 'primary' && <div className="field-callout">Verwendet den final berechneten Wert der stärksten Schaden- oder Heilwirkung dieser Fähigkeit.</div>}
                </div>
              )}
            </div>
          );
        })}
        {outcomes.length === 0 && <div className="empty compact outcome-empty"><strong>Noch keine Wirkung eingetragen.</strong><span>Wähle oben mindestens eine Wirkung aus.</span></div>}
      </div>
    </div>
  );
}
